import { auth } from '../services/firebase';

import { AuthProvider } from '../shared/constants';
import * as endpoints from '../services/redux/endpoints';
import axios from 'axios';
import config from '../envConfig';
import { Paths } from '../shared/paths';

interface AuthData {
  email: string;
  password: string;
  firstname?: string;
  lastname?: string;
}

interface SignInData {
  email: string;
  providerId: AuthProvider;
  uid: string;
  firstname?: string;
}

interface SignUpData {
  email: string;
  uid: string;
  providerId: AuthProvider;
  firstname: string;
  lastname: string;
}

const BASE_URL = config.apiBaseUrl;
const http = axios.create({ baseURL: BASE_URL });
const UNAUTHORIZED_STATUS = 401;

const getErrorStatusCode = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorData = error as {
    status?: number;
    response?: { status?: number };
  };

  if (typeof errorData.status === 'number') {
    return errorData.status;
  }

  if (typeof errorData.response?.status === 'number') {
    return errorData.response.status;
  }

  return undefined;
};

export function useFirebaseAuth() {
  const getToken = async (): Promise<string | undefined> => {
    return auth.currentUser?.getIdToken();
  };

  // const signUpWithPassword = async (data: AuthData): Promise<void> => {
  //   const response = await auth.createUserWithEmailAndPassword(
  //     data.email,
  //     data.password,
  //   );
  //   console.log(response);
  //   if (!response || !response.user) {
  //     throw new Error('Failed to sign up');
  //   }
  //
  //   const accessToken = await response.user.getIdToken();
  //
  //   await signUpApi(
  //     {
  //       email: response.user.email!,
  //       uid: response.user.uid,
  //       providerId: response.additionalUserInfo?.providerId as AuthProvider,
  //       firstname: data.firstname || response.user?.displayName || '',
  //       lastname: data.lastname || '',
  //     },
  //     accessToken,
  //   );
  //
  //   await sendEmailVerification(response?.user);
  // };

  const logInWithPassword = async (data: AuthData): Promise<void> => {
    const response = await auth.signInWithEmailAndPassword(
      data.email,
      data.password,
    );

    if (!response || !response.user) {
      throw new Error('Failed to log in');
    }

    const accessToken = await response.user.getIdToken();

    // try {
    //   await signInApi(
    //     {
    //       email: response.user.email,
    //       uid: response.user.uid,
    //       providerId: response.additionalUserInfo?.providerId as AuthProvider,
    //     },
    //     accessToken,
    //   );
    // } catch (e) {
    //   await auth.signOut();
    //
    //   console.error('Failed to login', e);
    //   throw e;
    // }
  };

  const resetPassword = async (email: string): Promise<void> => {
    return auth.sendPasswordResetEmail(email);
  };

  const confirmPasswordReset = async (
    oobCode: string,
    newPassword: string,
  ): Promise<void> => {
    await auth.verifyPasswordResetCode(oobCode);
    await auth.confirmPasswordReset(oobCode, newPassword);
  };

  // const signInWithGoogle = async (): Promise<void> => {
  //   const response = await auth.signInWithPopup(googleProvider);
  //
  //   if (!response || !response.user) {
  //     throw new Error('Failed to log in');
  //   }
  //
  //   const accessToken = await response.user.getIdToken();
  //
  //   await signInApi(
  //     {
  //       email: response.user.email!,
  //       uid: response.user.uid,
  //       providerId: response.additionalUserInfo?.providerId as AuthProvider,
  //       firstname: response.user.displayName || '',
  //     },
  //     accessToken,
  //   );
  // };

  const logOut = (): Promise<void> => {
    return auth.signOut();
  };

  const clearCookies = () => {
    const cookies = document.cookie ? document.cookie.split(';') : [];

    cookies.forEach((cookie) => {
      const cookieName = cookie.split('=')[0]?.trim();

      if (cookieName) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    });
  };

  const forceLogoutAndRedirect = async (): Promise<void> => {
    try {
      await auth.signOut();
    } catch {
      // Continue cleanup even if Firebase signout fails.
    }

    localStorage.clear();
    sessionStorage.clear();
    clearCookies();
    window.location.replace(Paths.signIn);
  };

  const postSignIn = async (
    data: SignInData,
    accessToken: string,
  ): Promise<any> => {
    const response = await http.post(endpoints.Account.SIGNIN, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return response.data;
  };

  const signInApi = async (data: SignInData): Promise<any> => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error('User is not authenticated in Firebase');
    }

    const token = await currentUser.getIdToken(true);

    try {
      return await postSignIn(data, token);
    } catch (error) {
      const statusCode = getErrorStatusCode(error);

      if (statusCode !== UNAUTHORIZED_STATUS) {
        throw error;
      }

      const refreshedToken = await currentUser.getIdToken(true);

      try {
        return await postSignIn(data, refreshedToken);
      } catch (retryError) {
        if (getErrorStatusCode(retryError) === UNAUTHORIZED_STATUS) {
          await forceLogoutAndRedirect();
        }

        throw retryError;
      }
    }
  };

  // const signUpApi = async (
  //   data: SignUpData,
  //   accessToken: string,
  // ): Promise<any> => {
  //   return http
  //     .post(endpoints.Account.SIGNUP, data, {
  //       headers: { Authorization: 'Bearer ' + accessToken },
  //     })
  //     .then(({ data }) => data);
  // };

  const changePassword = async (
    email: string,
    currentPassword: string,
    newPassword: string,
  ): Promise<void> => {
    await auth.signInWithEmailAndPassword(email, currentPassword);
    return auth.currentUser.updatePassword(newPassword);
  };

  return {
    signInApi,
    getToken,
    logInWithPassword,
    resetPassword,
    confirmPasswordReset,
    logOut,
    changePassword,
  };
}
